import { Flex, Text, Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { keyframes } from '@emotion/react';

import RefreshIcon from 'components/Icons/RefreshIcon';
import { getAllInstrumentOutputsStatus } from 'redux/DashboardSliceV2';
import { STATUS } from 'projectConstants';
import { font, neutral, radius, text as textToken } from 'styles/revampTokens';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5); }
  70% { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const formatElapsed = (syncedAt: number, now: number) => {
  const minutes = Math.max(0, Math.round((now - syncedAt) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 min ago';
  return `${minutes} min ago`;
};

interface Properties {
  onRefresh?: () => void;
}

/** "Live · synced X min ago" pill with a manual refresh action, shown next to the page title. */
const LiveSyncBadge = ({ onRefresh }: Properties) => {
  const outputsStatus = useSelector(getAllInstrumentOutputsStatus);
  const [syncedAt, setSyncedAt] = useState<number>(Date.now());
  const [now, setNow] = useState<number>(Date.now());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const previousStatus = useRef(outputsStatus);

  useEffect(() => {
    if (outputsStatus === STATUS.SUCCESS && previousStatus.current !== STATUS.SUCCESS) {
      setSyncedAt(Date.now());
      setIsRefreshing(false);
    }
    previousStatus.current = outputsStatus;
  }, [outputsStatus]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    onRefresh();
  };

  return (
    // Same corner as every other chip/control in the app (radius.control) —
    // this was the one pill left on the full-round 50px radius from before
    // the neutral-chrome pass, so it read as a leftover from the old theme
    // next to the search field, buttons and category pills around it.
    <Flex
      bg={neutral.surface}
      border={`1px solid ${neutral.border}`}
      borderRadius={radius.control}
      boxShadow='0px 1px 2px rgba(18, 24, 38, 0.06)'
      transform='scale(0.95)'
      transformOrigin='right center'
      alignItems='center'
      gap='8px'
      pl='12px'
      pr='7px'
      py='4px'
      w='fit-content'
      flexShrink={0}
    >
      {/* The status dot and refresh button stay circular — that's their own
          shape (a status indicator, a round icon button), not a corner-radius
          choice the chrome pass governs. */}
      <Box position='relative' width='9px' height='9px' flexShrink={0}>
        <Box
          position='absolute'
          inset={0}
          borderRadius='50%'
          bg='#22C55E'
          animation={`${pulse} 2s ease-out infinite`}
        />
        <Box position='absolute' inset={0} borderRadius='50%' bg='#22C55E' />
      </Box>
      <Text fontFamily={font.body} fontWeight='500' fontSize='13px' color={textToken.secondary}>
        <Text as='span' fontFamily={font.body} fontWeight='700' color={textToken.primary}>
          Live
        </Text>{' '}
        · synced {formatElapsed(syncedAt, now)}
      </Text>
      <Box
        as='button'
        type='button'
        onClick={handleRefresh}
        display='flex'
        alignItems='center'
        justifyContent='center'
        width='27px'
        height='27px'
        borderRadius='50%'
        cursor={isRefreshing ? 'default' : 'pointer'}
        transition='background 0.15s ease'
        _hover={{ bg: neutral.surfaceHover }}
        title='Refresh'
      >
        <Box animation={isRefreshing ? `${spin} 0.7s linear infinite` : undefined} display='flex'>
          <RefreshIcon width={14} height={14} color={textToken.secondary} />
        </Box>
      </Box>
    </Flex>
  );
};

export default LiveSyncBadge;
